import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { axiosInstance } from '@/modules/shared/lib/axios'

import { VehicleModel } from '@/app/models'

const useVehicleService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getVehicleGroups = async ({ companyId }: { companyId: string }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(`/api/${companyId}/vehicle/groups`)
      return res.data || []
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getVehiclesInGroup = async ({
    companyId,
    groupId,
  }: {
    companyId: string
    groupId: number
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/vehicle/vehiclesInGroup?groupId=${groupId}`,
      )
      return res.data || []
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const addVehicle = async ({
    companyId,
    vehicle,
  }: {
    companyId: string
    vehicle: Partial<VehicleModel>
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`/api/${companyId}/vehicle/save`, {
        vehicle: vehicle,
      })
      return res
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const addVehicleGroup = async ({
    companyId,
    name,
  }: {
    companyId: string
    name: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/vehicle/addGroup`,
        {
          name,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const addVehicleToGroup = async ({
    companyId,
    groupId,
    vehicleIds,
  }: {
    companyId: string
    groupId: number
    vehicleIds: string[]
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/vehicle/addVehicleToGroup`,
        {
          groupId,
          vehicles: vehicleIds.map(Number),
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const removeVehicleFromGroup = async ({
    companyId,
    groupId,
    vehicleIds,
  }: {
    companyId: string
    groupId: number
    vehicleIds: string[]
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/vehicle/removeVehicleFromGroup`,
        {
          groupId,
          vehicles: vehicleIds.map(Number),
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const deleteVehicleGroups = async ({
    companyId,
    groupIds,
  }: {
    companyId: string
    groupIds: number[]
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/vehicle/removeVehicleGroups`,
        {
          groupIds,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const getVehicle = async ({
    companyId,
    vehicleId,
  }: {
    companyId: string
    vehicleId: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/vehicle/${vehicleId}`,
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const getVehiclesWithLocks = async ({ companyId }: { companyId: string }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/vehicle/vehiclesWithLocks`,
      )
      return res.data || []
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getVehicleLocks = async ({
    companyId,
    vehicleId,
  }: {
    companyId: string
    vehicleId: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/vehicle/vehicleLocks?&id=${vehicleId}`,
      )
      return res.data || []
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const activateVehicle = async ({
    companyId,
    vehicleIds,
    setActive,
  }: {
    companyId: string
    vehicleIds: string[]
    setActive: boolean
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/vehicle/activate`,
        {
          companyId: companyId,
          vehicles: vehicleIds,
          activate: setActive,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  return {
    getVehicleGroups,
    getVehiclesInGroup,
    addVehicle,
    addVehicleGroup,
    addVehicleToGroup,
    removeVehicleFromGroup,
    deleteVehicleGroups,
    getVehicle,
    getVehicleLocks,
    getVehiclesWithLocks,
    activateVehicle,
  }
}

export default useVehicleService
